<template>
  <div class="contents">
    <audio
      ref="audioRef"
      :src="playerStore.track?.audioUrl || ''"
      @play="playerStore.setPlaying(true)"
      @pause="playerStore.setPlaying(false)"
      @ended="playerStore.setPlaying(false)"
      @timeupdate="onTimeUpdate"
      @durationchange="onDurationChange"
    />
    <div
      v-if="playerStore.hasTrack"
      class="bg-[#0f2929] text-white shrink-0 border-t border-white/5"
    >
    <div
      ref="progressTrackRef"
      class="w-full h-1 bg-white/10 cursor-pointer hover:bg-white/20 transition-colors"
      :title="progressLabel"
      @click="onProgressClick"
    >
      <span
        class="block h-full bg-[#ecd79f]/80 transition-[width] pointer-events-none"
        :style="{ width: `${progressPercent}%` }"
      />
    </div>
    <div class="flex items-center justify-between max-w-[1920px] mx-auto px-4 py-3 md:px-6 md:py-4">
      <div class="flex items-center gap-3 md:gap-4 w-1/3 min-w-0">
        <div class="w-10 h-10 md:w-12 md:h-12 rounded-lg overflow-hidden bg-black/20 shrink-0 relative group">
          <img
            :src="playerStore.track?.coverUrl || ''"
            :alt="playerStore.track?.title"
            class="w-full h-full object-cover opacity-80 group-hover:opacity-40 transition-opacity"
          >
          <div class="absolute inset-0 flex items-center justify-center gap-0.5" :class="{ playing: playerStore.isPlaying }">
            <div class="bar w-0.5 bg-[#ecd79f] h-full rounded-full" />
            <div class="bar w-0.5 bg-[#ecd79f] h-full rounded-full" />
            <div class="bar w-0.5 bg-[#ecd79f] h-full rounded-full" />
            <div class="bar w-0.5 bg-[#ecd79f] h-full rounded-full" />
          </div>
        </div>
        <div class="min-w-0 overflow-hidden">
          <h4 class="font-bold text-sm text-[#f2f4f4] truncate mb-0.5">
            {{ playerStore.track?.title || '—' }}
          </h4>
          <div class="flex items-center gap-2">
            <span class="text-[11px] text-white/50 truncate">
              {{ playerStore.track?.authorName || '—' }}
            </span>
          </div>
        </div>
        <button type="button" class="text-white/30 hover:text-[#ecd79f] transition-colors p-1">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
        </button>
      </div>

      <div class="flex flex-col items-center gap-1">
        <div class="flex items-center gap-4 md:gap-6">
          <button type="button" class="text-white/40 hover:text-white transition-colors p-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 3h5v5"/><path d="M8 3H3v5"/><path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3"/><path d="m15 9 6-6"/></svg>
          </button>
          <button type="button" class="text-white/70 hover:text-white transition-colors p-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h2v12H6zm3.5 6 8.5 6V6z"/></svg>
          </button>

          <button
            type="button"
            class="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white text-[#1c4848] flex items-center justify-center hover:scale-105 active:scale-95 transition-all shadow-lg shadow-white/10"
            @click="togglePlay"
          >
            <template v-if="playerStore.isPlaying">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" class="ml-0.5"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
            </template>
            <template v-else>
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" class="ml-0.5"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            </template>
          </button>

          <button type="button" class="text-white/70 hover:text-white transition-colors p-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M6 18h2V6H6zm11-12v12l8.5-6z"/></svg>
          </button>
          <button type="button" class="text-white/40 hover:text-white transition-colors p-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
          </button>
        </div>
      </div>

      <div class="flex items-center justify-end gap-2 md:gap-3 w-1/3">
        <button
          type="button"
          class="text-white/60 hover:text-white p-1 shrink-0"
          :title="isMuted ? 'Unmute' : 'Mute'"
          @click="toggleMute"
        >
          <svg v-if="isMuted" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 5L6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>
        </button>
        <div class="hidden md:flex items-center gap-3 group">
          <div class="w-20 h-1 bg-white/10 rounded-full cursor-pointer relative overflow-hidden" @click="onVolumeTrackClick">
            <div
              class="absolute left-0 top-0 h-full rounded-full bg-white/50 group-hover:bg-[#ecd79f] transition-colors"
              :style="{ width: `${isMuted ? 0 : playerStore.volume * 100}%` }"
            />
          </div>
        </div>
        <div class="w-px h-8 bg-white/10 mx-1 hidden md:block" />
        <button type="button" class="text-white/40 hover:text-white transition-colors p-1">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
        </button>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { usePlayerStore } from '@/store/player/index.js';

const playerStore = usePlayerStore();
const { isMuted } = storeToRefs(playerStore);
const audioRef = ref(null);
const progressTrackRef = ref(null);
const currentTime = ref(0);
const duration = ref(0);

const progressPercent = computed(() =>
  duration.value > 0 ? (currentTime.value / duration.value) * 100 : 0
);

const progressLabel = computed(() => {
  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };
  return `${fmt(currentTime.value)} / ${fmt(duration.value)}`;
});

function onTimeUpdate() {
  if (audioRef.value) currentTime.value = audioRef.value.currentTime;
}

function onDurationChange() {
  if (audioRef.value) duration.value = audioRef.value.duration || 0;
}

function onProgressClick(e) {
  const el = progressTrackRef.value;
  const audio = audioRef.value;
  if (!el || !audio || !Number.isFinite(audio.duration)) return;
  const rect = el.getBoundingClientRect();
  const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
  audio.currentTime = pct * audio.duration;
  currentTime.value = audio.currentTime;
}

function tryPlay() {
  const el = audioRef.value;
  if (!el || !el.src) return;
  if (!playerStore.isPlaying) return;
  el.play().catch(() => playerStore.setPlaying(false));
}

watch(
  () => playerStore.track?.audioUrl ?? null,
  (src) => {
    currentTime.value = 0;
    duration.value = 0;
    const el = audioRef.value;
    if (!el) return;
    el.volume = playerStore.volume;
    if (src) {
      el.src = src;
      if (playerStore.isPlaying) {
        const p = el.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => {
            el.addEventListener('canplay', tryPlay, { once: true });
          });
        }
      }
    }
  },
  { immediate: true }
);

watch(
  () => playerStore.isPlaying,
  (isPlaying) => {
    const el = audioRef.value;
    if (!el || !el.src) return;
    if (isPlaying) {
      if (el.readyState >= 2) tryPlay();
    } else {
      el.pause();
    }
  },
  { immediate: true }
);

watch(
  () => playerStore.volume,
  (v) => {
    if (audioRef.value) audioRef.value.volume = v;
  },
  { immediate: true }
);

function togglePlay() {
  playerStore.toggle();
}

function toggleMute() {
  playerStore.toggleMute();
  const el = audioRef.value;
  if (el) el.volume = playerStore.volume;
}

function onVolumeTrackClick(e) {
  const rect = e.currentTarget.getBoundingClientRect();
  const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
  playerStore.setVolume(pct);
}

</script>

<style scoped>
@keyframes wave {
  0%, 100% { height: 20%; }
  50% { height: 100%; }
}
.playing .bar {
  animation: wave 1s ease-in-out infinite;
}
.playing .bar:nth-child(1) { animation-delay: 0s; }
.playing .bar:nth-child(2) { animation-delay: 0.1s; }
.playing .bar:nth-child(3) { animation-delay: 0.2s; }
.playing .bar:nth-child(4) { animation-delay: 0.3s; }
</style>
